import type { SortOrder } from 'antd/lib/table/interface';
import type dayjs from 'dayjs';
import { z } from 'zod';
import type { StoredUser } from '../../hooks/types';

export const TeamType = {
  LocalIMT: 'Local IMT',
  CostedIMT: 'Costed IMT',
  Section44: 'Section 44',
  EmergencyOperationsCentre: 'Emergency Operations Centre',
} as const;

/**
 * The record status of a team in the PRAS Database
 */
export const recordStatusSchema = z.enum(['Active', 'Deleted']);
export type RecordStatus = z.infer<typeof recordStatusSchema>;

/**
 * The status of a team as displayed in PRAS.
 */
export const archiveStatusSchema = z.enum(['Active', 'Archived']);
export type ArchiveStatus = z.infer<typeof archiveStatusSchema>;

type TeamTypeKey = keyof typeof TeamType;

export type TeamTypeValue = (typeof TeamType)[TeamTypeKey];

export const teamTypes = (
  manageNonLocalImt: boolean
): readonly TeamTypeValue[] => {
  if (manageNonLocalImt) {
    return [
      TeamType.LocalIMT,
      TeamType.Section44,
      TeamType.EmergencyOperationsCentre,
      TeamType.CostedIMT,
    ];
  }

  return [TeamType.LocalIMT];
};

/**
 * Local form updated Team Role Assignment.
 */
export type UpdatedTeamRoleAssignment = {
  readonly id: string;
  readonly teamRoleId: string;
  readonly teamRoleGroup?: string;
  readonly users?: readonly StoredUser[];
  // This is just used for Select option value, it should match the users
  readonly userIds?: readonly string[];
};

/**
 * Represents a Team locally in the form.
 */
export type TeamInForm = {
  readonly name: string;
  readonly teamType?: string;
  readonly teamRoleAssignments?: readonly UpdatedTeamRoleAssignment[];
  readonly startTime: dayjs.Dayjs;
  readonly duration: number;
  readonly internalOrderNumber: string;
};

export const roleGroupTypes: readonly string[] = [
  'Control',
  'Operations',
  'Planning',
  'Logistics',
  'Agency Liaison',
  'Investigation',
  'Public Information',
  'Intelligence',
  'Finance',
  'Operations (Air Ops)',
  'Operations (Field Ops)',
  'Emergency Operations Centre',
  'SOC',
] as const;

/**
 * Type of group that a role is within.
 */
export type RoleGroup = (typeof roleGroupTypes)[number];

export type VariableTeamSettings = {
  readonly dayShiftPeriod: readonly number[];
};

export type NameAndCallback = {
  readonly name: string;
} & { readonly onClick: () => void };

export type TeamSortBy = {
  readonly name?: SortOrder;
  readonly created_at?: SortOrder;
  readonly deleted_at?: SortOrder;
};

/**
 * The team name column max with in pixel of team table
 */
export const teamNameMaxWidth = 300;

export const teamsToDisplay = ['All', 'Active Only', 'Archived Only'] as const;

/**
 * The teams to show in the All Teams table
 */
export type TeamsToDisplay = (typeof teamsToDisplay)[number];
